import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, DoorOpen, KeyRound, Users } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import { API, trackEvent, formatApiError, QUIZ_URL } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1699058455521-2d3d52e04861?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHx3b21hbiUyMGdvbGZlciUyMHN3aW5nJTIwc3Vuc2V0JTIwY291cnNlfGVufDB8fHx8MTc5MTQwOTYyNnww&ixlib=rb-4.1.0&q=85";
const EXEC_IMG = "https://images.unsplash.com/photo-1697448524689-8dfd3d11071e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4Tk1ODB8MHwxfHNlYXJjaHwxfHxmZW1hbGUlMjBnb2xmZXIlMjB3YWxraW5nJTIwZmFpcndheSUyMGV4ZWN1dGl2ZXxlbnwwfHx8fDE3OTE0MDk2MjZ8MA&ixlib=rb-4.1.0&q=85";

const STATS = [
  { value: "60%+", label: "of golf's on-course growth since 2020 has come from women", source: "National Golf Foundation", id: "stat-growth" },
  { value: "70%", label: "of Fortune 1000 CEOs have done business with someone they met on the course", source: "Fortune survey", id: "stat-ceos" },
  { value: "80%", label: "of Fortune 500 executives say golf accelerated their careers", source: "Fortune survey", id: "stat-careers" },
];

const OFTEN_THINK = [
  "I need lessons first",
  "I need to get better",
  "I don't know the rules",
  "Everyone will judge me",
  "Golf isn't my thing",
];

const EXECS_KNOW = [
  "Presence matters",
  "Relationships matter",
  "Confidence matters",
  "Conversations matter",
  "Access matters",
];

const PILLARS = [
  { name: "Knowledge", desc: "The basics of the game, etiquette, terminology and what to expect." },
  { name: "Experience", desc: "Hands-on practice in a welcoming, low-pressure environment." },
  { name: "Confidence", desc: "Tools, tips and support to help you show up and belong." },
  { name: "Practice", desc: "Real situations, real conversations, real progress." },
];

export default function Women() {
  const [form, setForm] = useState({ name: "", email: "", program: "not-sure", note: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Women Belong in the Game — Wired 2 Win Golf";
    trackEvent("page_view", { page: "women" });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/program-interest`, form);
      setStatus("done");
      trackEvent("program_interest_submitted", { program: form.program });
    } catch (err) {
      setStatus("idle");
      setError(formatApiError(err));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <main data-testid="women-page">
      {/* HERO */}
      <section className="relative min-h-[85svh] flex items-end overflow-hidden">
        <img src={HERO_IMG} alt="Woman golfer mid-swing at golden hour" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-[linear-gradient(to_top,#0A0A0A_0%,transparent_100%)]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-20 pt-40 w-full">
          <Overline className="mb-6"><MaskedLine delay={0.1}>A Wired2Win initiative</MaskedLine></Overline>
          <h1 className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-7xl lg:text-8xl uppercase">
            <MaskedLine delay={0.25}>Women belong</MaskedLine>
            <MaskedLine delay={0.4}><span className="text-volt">in the game.</span></MaskedLine>
          </h1>
          <Reveal delay={0.7} y={24}>
            <p className="mt-6 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed">
              Not someday. Not after more lessons. Now — because the course is where presence, relationships and access are built.
            </p>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/10 bg-ink/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 md:grid-cols-3 gap-10">
          {STATS.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.1}>
              <div data-testid={s.id}>
                <p className="font-display font-black tracking-tighter text-6xl lg:text-7xl text-volt leading-none">{s.value}</p>
                <p className="text-white/70 text-sm sm:text-base leading-relaxed mt-4 max-w-xs">{s.label}</p>
                <p className="text-white/30 text-[11px] uppercase tracking-widest mt-3">Source: {s.source}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* REFRAME */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <Reveal>
          <Overline>The reframe</Overline>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none mt-4 mb-14 max-w-3xl">
            Same course. <span className="text-white/40">Completely different conversation.</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal>
            <div className="border border-white/10 bg-ink p-8 sm:p-10 h-full" data-testid="reframe-think">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-white/40 mb-8">What women often think</p>
              <ul className="space-y-5">
                {OFTEN_THINK.map((t) => (
                  <li key={t} className="text-white/50 text-base sm:text-lg line-through decoration-white/25">{t}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-volt/50 bg-volt/5 p-8 sm:p-10 h-full" data-testid="reframe-know">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-volt mb-8">What successful executives know</p>
              <ul className="space-y-5">
                {EXECS_KNOW.map((t) => (
                  <li key={t} className="text-white text-base sm:text-lg font-semibold flex items-center gap-3">
                    <Check className="w-4 h-4 text-volt shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-14 text-center">
            Golf opens doors. <span className="text-volt">Confidence walks through them.</span>
          </p>
        </Reveal>
      </section>

      {/* PROGRAM 1 — GRASS CEILING */}
      <section className="border-t border-white/10 bg-ink/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <Overline>Program 01 — for women new to the game</Overline>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl uppercase mt-4">
              Drive it through the <span className="text-volt">Grass Ceiling™</span>
            </h2>
            <p className="mt-6 text-white/60 text-base sm:text-lg leading-relaxed">
              Golf isn't about being the best player. It's about being present where relationships are built. This program removes the uncertainty — so you never have to sit out an opportunity again.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PILLARS.map((p, i) => (
                <div key={p.name} data-testid={`pillar-${p.name.toLowerCase()}`} className="border border-white/10 bg-obsidian p-5">
                  <p className="font-display font-bold text-lg tracking-tight text-volt">{p.name}</p>
                  <p className="text-white/50 text-sm leading-relaxed mt-2">{p.desc}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 font-display font-bold text-xl sm:text-2xl tracking-tight">
              You don't need to be great at golf. <span className="text-volt">You need to be comfortable around golf.</span>
            </p>
          </Reveal>
          <Reveal className="lg:col-span-6" delay={0.15}>
            <div className="border border-white/10 p-2 bg-obsidian">
              <img src="/images/w2w-grass-ceiling.webp" alt="Drive it Through the Grass Ceiling program overview" loading="lazy"
                data-testid="grass-ceiling-graphic"
                className="w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROGRAM 2 — PRESENCE TO IMPACT */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6 lg:order-2">
            <Overline>Program 02 — for women in leadership</Overline>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl uppercase mt-4">
              From presence <span className="text-volt">to impact.</span>
            </h2>
            <p className="mt-6 text-white/60 text-base sm:text-lg leading-relaxed">
              Don't survive the boys' club — architect it. This program treats the course as a live-fire laboratory for executive presence: authenticity over imitation, influence built your way.
            </p>
            <div className="mt-10 space-y-4">
              {[
                { icon: DoorOpen, t: "Don't fit in — architect", d: "Stop adapting to a room that wasn't designed for you. Learn to shape it." },
                { icon: KeyRound, t: "Decode your blueprint", d: "Your wiring isn't the obstacle — it's the advantage. We show you how to read it." },
                { icon: Users, t: "Presence under pressure", d: "Practice the moments that matter — with real stakes, real conversations, real progress." },
              ].map((f) => (
                <div key={f.t} className="flex gap-4 items-start border border-white/10 bg-ink p-5">
                  <f.icon className="w-5 h-5 text-volt shrink-0 mt-0.5" />
                  <div>
                    <p className="font-display font-bold tracking-tight">{f.t}</p>
                    <p className="text-white/50 text-sm leading-relaxed mt-1">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:order-1" delay={0.15}>
            <div className="space-y-6">
              <div className="border border-white/10 p-2 bg-obsidian">
                <img src={EXEC_IMG} alt="Woman golfer in executive attire on the fairway" loading="lazy"
                  data-testid="presence-exec-image"
                  className="w-full aspect-[16/10] object-cover" />
              </div>
              <div className="border border-white/10 p-2 bg-obsidian">
                <img src="/images/w2w-comfort-pillars.webp" alt="Comfort around golf: knowledge, experience, confidence, practice" loading="lazy"
                  data-testid="comfort-graphic"
                  className="w-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WIRED2WIN TIE-IN */}
      <section className="border-t border-white/10 bg-ink/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28 text-left">
          <Reveal>
            <Overline>Where Wired2Win fits</Overline>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-6xl uppercase mt-4 max-w-3xl">
              The assessment is the <span className="text-volt">blueprint decoder.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-white/60 text-base sm:text-lg leading-relaxed">
              Both programs are built on the Wired2Win assessment — a 30-question read on your motivators, strengths, ideal conditions and stress patterns. It's free, it takes about six minutes, and it tells us exactly how to coach <span className="text-white">you</span>.
            </p>
            <div className="mt-10">
              <motion.span whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="inline-block">
                <a href={QUIZ_URL} data-testid="women-quiz-cta"
                  onClick={() => trackEvent("cta_click", { location: "women" })}
                  className="group inline-flex items-center gap-2 bg-volt text-obsidian font-display font-bold px-7 py-4 text-base hover:bg-white transition-colors duration-200">
                  Take the Free Assessment
                  <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200" />
                </a>
              </motion.span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INTEREST FORM */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <Overline>Register your interest</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none mt-4">
              First cohorts <span className="text-volt">form soon.</span>
            </h2>
            <p className="mt-6 text-white/60 text-base leading-relaxed max-w-md">
              Tell us which program fits — or that you're not sure yet. Dave reads every note personally and will reach out with dates, format and details.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.15}>
            {status === "done" ? (
              <div data-testid="women-interest-success" className="border border-volt/40 bg-volt/5 p-10 flex flex-col items-start gap-4">
                <span className="w-12 h-12 bg-volt flex items-center justify-center"><Check className="w-6 h-6 text-obsidian" /></span>
                <h3 className="font-display font-bold text-3xl tracking-tight">You're on the list.</h3>
                <p className="text-white/60 text-sm leading-relaxed">Dave will be in touch personally with program details. Meanwhile, the free assessment above is the perfect warm-up.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-white/10 bg-ink p-8 sm:p-10 space-y-5" data-testid="women-interest-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="women-name" className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Name</label>
                    <input id="women-name" required value={form.name} onChange={set("name")} data-testid="women-name-input"
                      className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm focus:outline-none focus:border-volt transition-colors" placeholder="Your name" />
                  </div>
                  <div>
                    <label htmlFor="women-email" className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Email</label>
                    <input id="women-email" type="email" required value={form.email} onChange={set("email")} data-testid="women-email-input"
                      className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm focus:outline-none focus:border-volt transition-colors" placeholder="you@email.com" />
                  </div>
                </div>
                <div>
                  <label htmlFor="women-program" className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Which program?</label>
                  <select id="women-program" value={form.program} onChange={set("program")} data-testid="women-program-select"
                    className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm focus:outline-none focus:border-volt transition-colors text-white">
                    <option value="grass-ceiling">Drive it Through the Grass Ceiling™ — new to the game</option>
                    <option value="presence-to-impact">From Presence to Impact — women in leadership</option>
                    <option value="not-sure">Not sure yet — help me decide</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="women-note" className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Anything we should know? (optional)</label>
                  <textarea id="women-note" rows={4} value={form.note} onChange={set("note")} data-testid="women-note-input"
                    className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm focus:outline-none focus:border-volt transition-colors resize-none" placeholder="Your goals, your group, your questions..." />
                </div>
                {error && <p data-testid="women-error" className="text-red-400 text-xs">{error}</p>}
                <button type="submit" disabled={status === "loading"} data-testid="women-interest-submit"
                  className="w-full bg-volt text-obsidian font-display font-bold px-6 py-4 hover:bg-white transition-colors disabled:opacity-50">
                  {status === "loading" ? "Sending..." : "Register My Interest"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
